import { useAuth } from "@/shared/auth/auth.context";
import { AccountRole, canDo, type Permission, WorkspaceRole } from "@/shared/auth/permissions";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";

/// Returns whether the active workspace caller has `permission`.
///
/// Wraps the four-line auth-context dance:
///
///   const auth = useAuth();
///   const { workspace } = useActiveWorkspace();
///   const workspaceRoles = auth.memberships
///     .filter((m) => m.workspaceId === workspace.id)
///     .map((m) => m.role);
///   const ok = canDo(permission, { accountRole: auth.user?.accountRole ?? null, workspaceRoles });
///
/// Only valid inside the `app-layout/$workspaceSlug/*` route
/// subtree (anywhere `useActiveWorkspace` resolves). Routes outside that
/// scope (account settings, the workspace picker itself) should keep
/// calling `canDo()` directly with their own context.
///
/// Returns a fresh boolean every render, cheap to call multiple times
/// in the same component for different permissions.
export function useCanDo(permission: Permission): boolean {
  const auth = useAuth();
  const { workspace } = useActiveWorkspace();
  const workspaceRoles = auth.memberships
    .filter((m) => m.workspaceId === workspace.id)
    .map((m) => m.role);
  return canDo(permission, {
    accountRole: auth.user?.accountRole ?? null,
    workspaceRoles,
  });
}

/// Whether the signed-in user has an account-level `permission`. For surfaces
/// whose gate does not depend on the active workspace (settings pages and the
/// settings rail), where `useCanDo` would wrongly demand a workspace.
export function useCanDoAtAccountLevel(permission: Permission): boolean {
  const auth = useAuth();
  return canDo(permission, { accountRole: auth.user?.accountRole ?? null });
}

/// Whether the caller administers the active workspace: a workspace admin, or
/// an account owner/admin, which is the same pair the shore accepts wherever a
/// route is admin-gated rather than permission-gated.
///
/// Use this only for controls the shore itself restricts to admins, so the UI
/// does not offer an action the API will refuse. Anything expressible as a
/// permission belongs in `useCanDo` instead: roles are coarse, and hiding on
/// role where a permission exists is how the two drift.
export function useIsWorkspaceAdmin(): boolean {
  const auth = useAuth();
  const { workspace } = useActiveWorkspace();
  const accountRole = auth.user?.accountRole ?? null;
  if (accountRole === AccountRole.OWNER || accountRole === AccountRole.ADMIN) return true;
  return auth.memberships.some(
    (m) => m.workspaceId === workspace.id && m.role === WorkspaceRole.ADMIN,
  );
}
